import { useQuery } from "@apollo/client";
import {
  GET_DEVELOPER_PROFILE,
  SEARCH_DEVELOPER_PROJECTS,
} from "@/graphql/queries/realestateProjects";
import Image from "next/image";
import { RED_CDN, THUMBOR_CDN } from "@/common/config";
import css from "./page.module.scss";
import { Button, Rating } from "@aqar-app/react-ui";
import Icon from "@/components/icon";
import React, { useMemo, useRef } from "react";
import Link from "next/link";
import { financialFormat, formatShortenedPrice } from "@/lib/helpers";
import dynamic from "next/dynamic";
import Head from "next/head";
import AppLink from "@/components/new-resolver/app.ink";
import { getProjectURL } from "@/containers/project-screen/ProjectCard";
import { usePath } from "@/contexts/path/path";
import RedBreadcrumb from "../../../components/RedBreadCrumb/RedBreadCrumb";
import { trackCallDeveloper } from "@/lib/tracking/trackRegisterInterest";

const LeafletMap = dynamic(() =>
  import("@/components/Listing/ListingScreen/LeafletMap").then(
    (m) => m.LeafletMap,
  ),
);

function DeveloperProjectCard({ project, developer } = {}) {
  const {
    title,
    status,
    project_type,
    starting_price,
    address,
    cover_img,
    imgs,
  } = project;
  const { updateContext } = usePath();
  return (
    <AppLink
      onClick={() =>
        updateContext({
          developer: {
            ...project.real_estate_developer,
            ...developer,
          },
          project: {
            ...project,
            real_estate_developer: {
              ...project.real_estate_developer,
              ...developer,
            },
            url: getProjectURL({
              id: project.id,
              title: project.title,
              real_estate_developer: project.real_estate_developer,
            }),
          },
          unit: null,
        })
      }
      path={getProjectURL({
        id: project.id,
        title: project.title,
        real_estate_developer: project.real_estate_developer,
      })}
    >
      <div className={css.project}>
        <div className={css.cover}>
          <div className={css.overlay}>
            <div>
              <p>{status}</p>
              <p>{project_type}</p>
            </div>
            <p> يبدأ من{financialFormat(starting_price)} ريال</p>
          </div>
          <Image
            onError={(e) => {
              if (e.target?.tried) return;
              e.target.src = THUMBOR_CDN.replace(
                `{file}`,
                "villa-default.png",
              ).replace("{size}", "350x0");
              e.target.tried = true;
            }}
            unoptimized
            src={THUMBOR_CDN.replace(`{file}`, cover_img || imgs?.[0]).replace(
              "{size}",
              "350x0",
            )}
            alt=""
            fill
          />
        </div>
        <div className={css.midContent}>
          <div>
            <h2>{title}</h2>
          </div>
          <Image
            alt="cover"
            src={RED_CDN + "/" + developer?.logo_img}
            width={20}
            height={20}
          />
        </div>
        <div className={css.footer}>
          <p>{address}</p>
        </div>
      </div>
    </AppLink>
  );
}

const RealestateDeveloperPage = function RealestateDeveloperPage(props) {
  const mapRef = useRef(null);
  const { developer: { id: developerId } = {}, previousPath } = usePath();
  const { data } = useQuery(GET_DEVELOPER_PROFILE, {
    skip: !developerId,
    variables: {
      id: parseInt(developerId, 10),
    },
  });
  const { data: projectsData } = useQuery(SEARCH_DEVELOPER_PROJECTS, {
    skip: !developerId,
    variables: {
      id: parseInt(developerId, 10),
    },
  });

  const developer = data?.RealEstateDeveloper?.get || {};
  const {
    logo_img,
    name,
    projects_count,
    population,
    surfaces,
    about,
    units_count,
    cover_img,
    social_snapchat,
    social_instagram,
    social_linkedin,
    phone,
    social_tiktok,
    social_x,
  } = developer;

  const { list: projects } =
    projectsData?.RealEstateProjects?.searchDeveloperProjects || {};
  const socialLinks = useMemo(
    () =>
      [
        { icon: "Snapchat", link: social_snapchat },
        { icon: "Instagram", link: social_instagram },
        { icon: "tiktok-2", link: social_tiktok },
        { icon: "twitter-new-2", link: social_x },
        { icon: "Linkedin", link: social_linkedin },
      ].filter((s) => s.link),
    [
      social_snapchat,
      social_instagram,
      social_linkedin,
      social_tiktok,
      social_x,
    ],
  );
  return (
    <div className={css.developersProfile}>
      <Head>
        <title>{`المشاريع العقارية | ${name} | تطبيق عقار`}</title>
      </Head>
      <div className={css.root}>
        <div className={css.bc}>
          <RedBreadcrumb />
        </div>
        <div className={css.coverImage}>
          <Image src={RED_CDN + "/" + cover_img} alt="" fill />
        </div>
        <div className={css.developerInfo}>
          <div className={css.title}>
            <Image
              src={RED_CDN + "/" + logo_img}
              alt={name + " Logo"}
              width={40}
              height={40}
            />
            <div>
              <h1>{name}</h1>
              <div>
                <Rating value={90} count={5} />
              </div>
            </div>
          </div>
          <div className={css.contact}>
            <div className={css.social}>
              {socialLinks.map((s, i) => (
                <Link key={s.name} href={s.link} target="_blank">
                  <Icon name={s.icon} height={20} width={20} />
                </Link>
              ))}
            </div>
            <Button
              onClick={() => {
                trackCallDeveloper({
                  source: previousPath || encodeURI(document.referrer),
                });
                open("tel:" + phone);
              }}
              outline
              theme="info"
            >
              <Icon name="Phone-blue" height={20} width={20} />
              إتصال
            </Button>
          </div>
        </div>
        <div className={css.developerStats}>
          <div className={css.stat}>
            <Icon name="red-units" width={40} height={40} />
            <div>
              <p>عدد الوحدات</p>
              <strong>{units_count || 0}</strong>
            </div>
          </div>
          <div className={css.stat}>
            <Icon name="red-area-range" width={40} height={40} />
            <div>
              <p>مسطحات البناء</p>
              <strong>{surfaces || 0}</strong>
            </div>
          </div>
          <div className={css.stat}>
            <Icon name="red-price-range" width={40} height={40} />
            <div>
              <p>عدد المشاريع</p>
              <strong>{projects_count || 0}</strong>
            </div>
          </div>
          <div className={css.stat}>
            <Icon name="red-price-range" width={40} height={40} />
            <div>
              <p>عدد السكان</p>
              <strong>{population || 0}</strong>
            </div>
          </div>
        </div>

        <div className={css.about}>
          <h2>من نحن</h2>
          <p>{about}</p>
        </div>

        <div className={css.map}>
          <h2>خريطة المشاريع</h2>
          {projects && (
            <LeafletMap
              onMapReady={(map) => {
                try {
                  if (!map || !projects) return;
                  mapRef.current = map;
                  map.setView([24.7136, 46.6753], 10);
                  const bounds = new L.LatLngBounds(
                    projects
                      .filter((p) => p?.location?.lat && p?.location?.lng)
                      .map((p) => [p.location.lat, p.location.lng]),
                  );
                  for (const project of projects) {
                    const icon = L.divIcon({
                      className: css.icon,
                      html: `<div
                    class="${css.rect}"
                    style="--marker-color:${project.marker_color || "#713e2f"}"
                  >
                   ${
                     project.icon
                       ? `<img alt="icon" src="${
                           RED_CDN + "/" + project.icon
                         }" />`
                       : ""
                   }
                    <span>يبدأ من ${formatShortenedPrice(
                      project.starting_price,
                    )}</span>
                  </div>`,
                    });
                    L.marker([project.location.lat, project.location.lng], {
                      icon,
                    }).addTo(map);
                  }
                  if (projects.length === 1) {
                    const { lat, lng } =
                      projectsData?.RealEstateProjects?.searchDeveloperProjects
                        .list[0].location;
                    mapRef.current.setView([lat, lng], 15);
                    return;
                  }
                  if (bounds && projects.length > 1) map.fitBounds(bounds);
                } catch (e) {}
              }}
            />
          )}
        </div>

        <div className={css.projects}>
          <h2>المشاريع</h2>
          <div className={css.grid}>
            {projects?.map((p) => (
              <DeveloperProjectCard
                developer={data?.RealEstateDeveloper?.get}
                project={p}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealestateDeveloperPage;
